/**
 * Updates the recently viewed products in localStorage.
 *
 * Storage format: Array<{ id: string, variantId?: string, variantTitle?: string }>
 * Old format (string[]) is normalised on read for backward compatibility.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed list.
   * If the product is already in the list it is moved to the front and its
   * variant info is updated.
   * @param {string} productId
   * @param {string} [variantId]
   * @param {string} [variantTitle]
   */
  static addProduct(productId, variantId, variantTitle) {
    const id = String(productId);
    let products = this.getProducts();

    products = products.filter((p) => p.id !== id);

    /** @type {{ id: string, variantId?: string, variantTitle?: string }} */
    const entry = { id };
    if (variantId) {
      entry.variantId = String(variantId);
      entry.variantTitle = variantTitle || '';
    }

    products.unshift(entry);
    products = products.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
  }

  /**
   * Updates the saved variant for an already-viewed product without moving it
   * in the list. No-op if the product is not in the list.
   * @param {string} productId
   * @param {string} variantId
   * @param {string} [variantTitle]
   */
  static updateVariant(productId, variantId, variantTitle) {
    const id = String(productId);
    const products = this.getProducts();
    const entry = products.find((p) => p.id === id);
    if (!entry) return;

    entry.variantId = String(variantId);
    // Only overwrite variantTitle if a non-empty value is provided, to avoid
    // clearing a previously stored title when the event doesn't carry one.
    if (variantTitle) entry.variantTitle = variantTitle;

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the recently viewed products list, normalising old string[] entries.
   * @returns {Array<{ id: string, variantId?: string, variantTitle?: string }>}
   */
  static getProducts() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => (typeof item === 'string' ? { id: item } : item))
        .filter((item) => item && typeof item.id === 'string' && item.id);
    } catch {
      return [];
    }
  }
}
